import { GlobalListTable, getPageSize } from '@global-dax-ad-platform/dax-components';
import { defaultTableConfig } from '@global-dax-ad-platform/dax-components';
import {
  GlobalCard,
  GlobalCardHeader,
  GlobalCardHeaderCollapsible,
} from '@global-engineering-shared/gweb-material-global';
import {
  ArrowDownSmallIcon,
  ArrowUpSmallIcon,
} from '@global-engineering-shared/gweb-material-icons-global-next';
import {
  ListTableBottomToolbar,
  ListTableLoadMorePagination,
  ListTableProvider,
  useListTable,
} from '@global-engineering-shared/gweb-material-lab';
import { Box, Typography } from '@global-engineering-shared/gweb-material-ui';
import { Grid } from '@material-ui/core';
import { useState } from 'react';

import useStyles from 'components/EntityListTable/EntityListTable.styles';
import LoadableComponent from 'components/LoadableComponent';
import { ITableConfig } from 'components/Table/types/table';
import { usePaginatedItemsContext } from 'context/PaginatedItems/context/PaginatedItemsProvider';
import { PaginatedItems } from 'context/PaginatedItems/types/PaginatedItems';
import { TTableEntity } from 'types/tableEntityType';

interface IEntityListTableProps {
  tableConfig: ITableConfig;
  id: string;
}

const EntityListTable: React.FC<IEntityListTableProps> = ({ tableConfig, id }) => {
  const {
    statusBorderColor,
    columns,
    collapsableTitle,
    handleRowClick,
    isCollapsable,
    tableTitle,
    secondaryFooterComponents,
  } = tableConfig;

  const classes = useStyles({ borderColour: statusBorderColor });
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const {
    items,
    isInitialLoading,
    totalElements,
    totalPages,
    loadMore,
    pageNumber,
    canLoadMore,
    isLoadMoreLoading,
  } = usePaginatedItemsContext() as PaginatedItems<TTableEntity>;

  const shouldShowContent = !isCollapsable || isExpanded;

  const instance = useListTable<TTableEntity>({
    data: items,
    columns: columns,
    rowCount: totalElements,
    getRowId: (row) => row.spaceId,
    pageCount: totalPages,
    ...defaultTableConfig,
    state: {
      pagination: {
        pageIndex: pageNumber - 1,
        pageSize: getPageSize(totalElements, pageNumber - 1),
      },
    },
    onPaginationChange: loadMore,
    meta: {
      ...defaultTableConfig.meta,

      loading: isLoadMoreLoading,
      canLoadMore: canLoadMore,
    },
    componentProps: {
      ...defaultTableConfig.componentProps,
      ListTableHeadSortLabelProps: {
        sortAscIcon: <ArrowUpSmallIcon />,
        sortDescIcon: <ArrowDownSmallIcon />,
      },
    },
  });

  return (
    <Box>
      <ListTableProvider instance={instance}>
        {tableTitle && (
          <Typography className={classes.tableTitle} variant="body1" fontWeight="bold">
            {tableTitle}
          </Typography>
        )}

        {isCollapsable ? (
          <GlobalCard className={classes.card}>
            <GlobalCardHeader>
              <GlobalCardHeaderCollapsible onOpen={toggleExpand} open={isExpanded} />
              <span className={classes.header}>{`${totalElements} ${collapsableTitle}`}</span>
            </GlobalCardHeader>

            {shouldShowContent && (
              <LoadableComponent loading={isInitialLoading}>
                <Box className={classes.tableWrapper}>
                  <GlobalListTable<TTableEntity> handleRowClick={handleRowClick} scrollId={id} />
                </Box>
              </LoadableComponent>
            )}
          </GlobalCard>
        ) : (
          <Box className={classes.card}>
            <LoadableComponent loading={isInitialLoading}>
              <Box className={classes.tableWrapper}>
                <GlobalListTable<TTableEntity> handleRowClick={handleRowClick} scrollId={id} />
              </Box>
            </LoadableComponent>
          </Box>
        )}

        {shouldShowContent && (
          <ListTableBottomToolbar>
            <Grid container className={classes.tableFooterWrapper}>
              <Grid item>
                <ListTableLoadMorePagination />
              </Grid>
              <Grid item>
                <Grid container className={classes.tableFooterWrapper}>
                  {secondaryFooterComponents}
                </Grid>
              </Grid>
            </Grid>
          </ListTableBottomToolbar>
        )}
      </ListTableProvider>
    </Box>
  );
};

export default EntityListTable;
